import type {
	PathFormatContext,
	PathMapPathDef,
	ScaledPathParam,
} from '@/types/coreX6/draw/pathMap.types';

/**
 * Карта SVG‑путей, используемых рендерером.
 */
export default class PathMap {
	pathMap: Record<string, PathMapPathDef>;

	constructor() {
		/**
		 * Карта описаний путей
		 *
		 * <h1>Описание пути</h1>
		 * Параметризованный путь задаётся так:
		 * <pre>
		 * 'GATEWAY_PARALLEL': {
		 *   d: 'm {mx},{my} {e.x0},0 0,{e.x1} {e.x1},0 0,{e.y0} -{e.x1},0 0,{e.y1} ' +
		 *   '-{e.x0},0 0,-{e.y1} -{e.x1},0 0,-{e.y0} {e.x1},0 z',
		 *   height: 17.5,
		 *   width:  17.5,
		 *   heightElements: [2.5, 7.5],
		 *   widthElements: [2.5, 7.5]
		 * }
		 * </pre>
		 * <p>Важно указывать корректные <b>height и width</b>, потому что масштабирование
		 * основано на соотношении этих значений и целевого размера
		 * (/!\ координаты x/y масштабируются разными коэффициентами).</p>
		 * <p>Массивы '<b>heightElements</b>' и '<b>widthElements</b>' содержат значения, которые масштабируются.
		 * Масштабирование выполняется по вычисленным коэффициентам.
		 * Координаты по оси y должны быть в <b>heightElements</b> и масштабируются по коэффициенту y.
		 * В параметризованном пути значения доступны через объект 'e' в фигурных скобках:
		 *   <ul>
		 *    <li>для оси y: {e.y0}, {e.y1}, ...</li>
		 *    <li>для оси x: {e.x0}, {e.x1}, ...</li>
		 *   </ul>
		 *   Числа x0, x1 и y0, y1 соответствуют индексам в массивах.
		 * </p>
		 */
		this.pathMap = {
			PICTO_ACTOR: {
				d: 'm {mx},{my} m 5.5 9.5 h 8 m -4 3.5 l 4 5 m -4 -5 l -4 5 m 4 -11.5 v 6.5 m 0 -12.5 a 0.5 0.5 90 0 0 0 6 a 0.5 0.5 90 0 0 0 -6 z',
				height: 18,
				width: 8,
				heightElements: [],
				widthElements: [],
			},

			PICTO_ROLE: {
				d: 'm {mx},{my} m 5 4.5 l 8 0 a 1 1 90 0 1 0 9 l -8 0 a 1 1 90 0 1 0 -9 m 8 9 a 0.5 0.5 90 0 1 0 -9',
				height: 9,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_CONTRACT: {
				d: 'm {mx},{my} m 1.5 2.5 l 15 0 l 0 13 l -15 0 z m 0 4 l 15 0 m -15 5 l 15 0',
				height: 13,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_REPRESENTATION: {
				d: 'm {mx},{my} m 1.5 2.5 l 15 0 l 0 11.5 c -7.5 -5 -7.5 5 -15 0 z m 0 4 l 15 0',
				height: 13,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_PRODUCT: {
				d: 'm {mx},{my} m 1.5 2.5 l 15 0 l 0 13 l -15 0 z m 0 4 l 8 0 m 0 -4 l 0 4',
				height: 13,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_COMPONENT: {
				d: 'm {mx},{my} m 4.5 4.5 l 0 -3 l 12 0 l 0 15 l -12 0 l 0 -3 m -3 -3 l 6 0 l 0 3 l -6 0 z m 3 0 l 0 -3 m -3 -3 l 6 0 l 0 3 l -6 0 z',
				height: 15,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_COLLABORATION: {
				d: 'm {mx},{my} m 0.5 9 a 1 1 90 0 0 11 0 a 1 1 90 0 0 -11 0 m 17 0 a 1 1 90 0 0 -11 0 a 1 1 90 0 0 11 0',
				height: 11,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_EVENT: {
				d: 'm {mx},{my} m 0.5 3.5 l 12 0 c 6.5 0 6.5 11 0 11 l -12 0 l 3.5 -5.5 z',
				height: 11,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_FUNCTION: {
				d: 'm {mx},{my} m 9 2 l -6.5 5.5 l 0 8.5 l 6.5 -5.5 l 6.5 5.5 l 0 -8.5 z',
				height: 14,
				width: 13,
				heightElements: [],
				widthElements: [],
			},

			PICTO_PROCESS: {
				d: 'm {mx},{my} m 0.5 6.5 l 9 0 l 0 -4 l 8 6.5 l -8 6.5 l 0 -4 l -9 0 z',
				height: 13,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_GROUPING: {
				d: 'm {mx},{my} M 2 8 L 22 8 L 22 22 L 2 22 Z M 2 3 L 2 8 L 14.666666666666666 8 L 14.666666666666666 3 Z',
				height: 24,
				width: 24,
				heightElements: [],
				widthElements: [],
			},
			PICTO_LOCATION: {
				d: 'm {mx},{my} m 8,0 c 2.6,0 5,2.6 5,5.8 c 0,4.4 -5,10 -5,10 s -5,-5.6 -5,-10 c 0,-3.2 2.4,-5.8 5,-5.8 z',
				height: 16,
				width: 16,
				heightElements: [],
				widthElements: [],
			},
			PICTO_AND_JUNCTION: {
				d: 'm {mx},{my} m 9 3 a 6 6 0 1 1 0 12 a 6 6 0 1 1 0 -12 z',
				height: 18,
				width: 18,
				heightElements: [],
				widthElements: [],
			},
			PICTO_OR_JUNCTION: {
				d: 'm {mx},{my} m 9 3 a 6 6 0 1 1 0 12 a 6 6 0 1 1 0 -12 z',
				height: 18,
				width: 18,
				heightElements: [],
				widthElements: [],
			},
			PICTO_NOTE: {
				d: 'm {mx},{my} m 1.5 1.5 h 15 v 11 l -3 3 h -12 z m 3.5 4 h 8 m -8 3 h 8 m -8 3 h 6 z',
				height: 18,
				width: 18,
				heightElements: [],
				widthElements: [],
			},

			PICTO_INTERFACE: {
				d: 'm {mx},{my} m 7.5 9.5 h -7 m 17 0 a 5 5 90 0 0 -10 0 a 5 5 90 0 0 10 0 z',
				height: 10,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_SERVICE: {
				d: 'm {mx},{my} m 5 4.5 l 8 0 a 1 1 90 0 1 0 9 l -8 0 a 1 1 90 0 1 0 -9',
				height: 9,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_INTERACTION: {
				d: 'm {mx},{my} m 10.5 2 q 6 0 6 7 q 0 7 -6 7 z m -3 14 q -6 0 -6 -7 q 0 -7 6 -7 z',
				height: 14,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_OBJECT: {
				d: 'm {mx},{my} m 1.5 2.5 l 15 0 l 0 13 l -15 0 z m 0 4 l 15 0',
				height: 13,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_CAPABILITY: {
				d: 'm {mx},{my} m 16.5 1.5 l -5 0 l 0 5 l -5 0 l 0 5 l -5 0 l 0 5 l 15 0 z m -10 10 l 10 0 m -5 -5 l 0 10 m -5 -5 l 0 5 m 5 -10 l 5 0',
				height: 15,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_GOAL: {
				d: 'm {mx},{my} m 8 1.03 a 7 7 0 1 0 0 14 a 7 7 0 1 0 0 -14 m 0 3 a 4 4 0 1 0 0 8 a 4 4 0 1 0 0 -8 m 0 3 a 1 1 0 1 0 0 2 a 1 1 0 1 0 0 -2',
				height: 14,
				width: 14,
				heightElements: [],
				widthElements: [],
			},

			PICTO_DRIVER: {
				d: 'm {mx},{my} m 8 1 l 0 7 m -5.5 -5 l 5.5 5 l 5.5 -5 m -11.5 5 a 6 6 0 1 0 12 0 a 6 6 0 1 0 -12 0 m 6 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0 m -4.5 5 l 4.5 -5 l 0 7 m 0 -7 l 4.5 5 m -12 -5 l 15 0',
				height: 14,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_STAKHOLDER: {
				d: 'm {mx},{my} m 5 4.5 l 8 0 a 1 1 90 0 1 0 9 l -8 0 a 1 1 90 0 1 0 -9 m 8 9 a 0.5 0.5 90 0 1 0 -9',
				height: 9,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_ASSESSMENT: {
				d: 'm {mx},{my} m 6.281 3.317 c 1.756 -1.756 4.608 -1.756 6.364 0 c 1.756 1.756 1.756 4.608 0 6.364 c -1.756 1.756 -4.608 1.756 -6.364 0 c -1.756 -1.756 -1.756 -4.608 0 -6.364 z m -4.243 10.607 l 4.243 -4.243',
				height: 11,
				width: 11,
				heightElements: [],
				widthElements: [],
			},

			PICTO_OUTCOME: {
				d: 'm {mx},{my} m 0.75 8.879 a 6.152 6.152 0 1 0 12.304 0 a 6.152 6.152 0 1 0 -12.304 0 m 2.637 0 a 3.515 3.515 0 1 0 7.03 0 a 3.515 3.515 0 1 0 -7.03 0 m 3.515 0 l 6.151 -6.152 l 0 -1.757 m -6.151 7.03 l 0 1.758 m 0.878 -1.758 l -1.757 0 m 5.273 -6.152 l 2.197 0',
				height: 12,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_PRINCIPLE: {
				d: 'm {mx},{my} m 13.5 4.25 c 0 -1.518 -1.232 -2.75 -2.75 -2.75 l -5.5 0 c -1.518 0 -2.75 1.232 -2.75 2.75 l 0 7.5 c 0 1.518 1.232 2.75 2.75 2.75 l 5.5 0 c 1.518 0 2.75 -1.232 2.75 -2.75 l 0 -7.5 z m -4.914 -1.316 l 0 7 m 0 2 l 0 1',
				height: 10,
				width: 11,
				heightElements: [],
				widthElements: [],
			},

			PICTO_REQUIREMENT: {
				d: 'm {mx},{my} m 5.118 4.5 l -4.118 7 l 9.882 0 l 4.118 -7 l -9.882 0',
				height: 7,
				width: 14,
				heightElements: [],
				widthElements: [],
			},

			PICTO_CONSTRAINT: {
				// Bounds: x 0.5..15.5 (w15), y 4.25..11.75 (h7.5) — замок/ограничение
				d: 'm {mx},{my} m 3.147 11.75 l 4.412 -7.5 l -2.647 0 l -4.412 7.5 l 2.647 0 z m 1.765 -7.5 l -4.412 7.5 l 10.588 0 l 4.412 -7.5 l -10.588 0',
				height: 8,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_MEANING: {
				// Bounds: ~0..18 (w18), ~1..15 (h14) — лампочка
				d: 'm {mx},{my} m 9.664 1 c 2.169 0 3.693 1.834 4.451 3.018 c 0.358 0.56 0.548 1.21 0.548 1.874 c 0.001 0.152 0.001 0.307 0.001 0.452 c 0 0.42 -0.167 0.823 -0.464 1.12 c -0.591 0.591 -1.536 1.536 -1.536 1.536 c 0 0 0.386 0.193 0.818 0.409 c 0.653 0.326 0.918 1.12 0.591 1.773 c -0.029 0.058 -0.057 0.114 -0.084 0.169 c -0.215 0.43 -0.498 0.822 -0.838 1.162 c -0.276 0.276 -0.619 0.619 -0.905 0.905 c -0.385 0.386 -0.83 0.706 -1.318 0.95 c -0.181 0.091 -0.375 0.188 -0.556 0.278 c -0.43 0.215 -0.947 0.156 -1.317 -0.151 c -0.716 -0.593 -1.805 -1.495 -1.805 -1.495 c 0 0 -0.379 0.477 -0.766 0.966 c -0.478 0.602 -1.312 0.788 -2 0.444 c -0.117 -0.059 -0.231 -0.116 -0.334 -0.167 c -0.298 -0.149 -0.486 -0.453 -0.486 -0.786 c 0 -0.592 0 -1.457 0 -1.457 c 0 0 0 0.085 0 0.214 c 0 0.232 -0.13 0.444 -0.337 0.55 c -0.207 0.105 -0.455 0.085 -0.642 -0.052 c -0.191 -0.139 -0.397 -0.289 -0.604 -0.44 c -0.976 -0.711 -1.219 -2.065 -0.551 -3.071 c 0.076 -0.113 0.125 -0.187 0.133 -0.199 c 0.001 -0.001 0.002 -0.003 0.002 -0.004 c 0.032 -0.047 0.671 -0.998 0.671 -0.998 c 0 0 -0.463 -0.554 -0.901 -1.078 c -0.468 -0.56 -0.56 -1.344 -0.234 -1.997 c 0.237 -0.473 0.463 -0.925 0.463 -0.925 c 0 0 0.099 -0.147 0.221 -0.329 c 0.294 -0.438 0.709 -0.782 1.193 -0.99 c 0.05 -0.021 0.101 -0.043 0.152 -0.065 c 0.91 -0.391 1.945 -0.361 2.83 0.082 c 0.338 0.169 0.604 0.302 0.604 0.302 l 0 1 c 0 0 0.384 -0.655 0.712 -1.216 c 0.301 -0.513 0.735 -0.935 1.257 -1.22 c 0.493 -0.269 1.031 -0.564 1.031 -0.564',
				height: 14,
				width: 18,
				heightElements: [],
				widthElements: [],
			},

			PICTO_VALUE: {
				// Bounds: x 0.5..15.5 (w15), y 3..13 (h10) — эллипс
				d: 'm {mx},{my} m 0.5 8 a 7.5 5 0 1 0 15 0 a 7.5 5 0 1 0 -15 0',
				height: 10,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_RESOURCE: {
				d: 'm {mx},{my} m 15.5 7 l 0 -2 l -1.5 -1.5 l -12 0 l -1.5 1.5 l 0 8 l 1.5 1.5 l 12 0 l 1.5 -1.5 l 0 -2 l 1.5 0 l 0.5 -0.5 l 0 -3 l -0.5 -0.5 z m -8 -2 l 0 8 m -4 -8 l 0 8 m 2 0 l 0 -8 m 10 2 l 0 4',
				height: 11,
				width: 17,
				heightElements: [],
				widthElements: [],
			},

			PICTO_VALUE_STREAM: {
				d: 'm {mx},{my} m 17 9 l -5 -6.5 l -11 0 l 5 6.5 l -5 6.5 l 11 0 z',
				height: 13,
				width: 16,
				heightElements: [],
				widthElements: [],
			},

			PICTO_COURSE_OF_ACTION: {
				d: 'm {mx},{my} m 6.5 6 a 1 1 90 0 0 11 0 a 1 1 90 0 0 -11 0 m 2.5 0 a 1 1 90 0 0 6 0 a 1 1 90 0 0 -6 0 m 2.5 0.5 a 1 1 90 0 0 1 0 a 1 1 90 0 0 0 -1 a 1 1 90 0 0 -1 0 a 1 1 90 0 0 0 1 m -3.5 3.5 l -0.5 4 l -3.5 -3.5 z m -2.5 1.5 q -3.5 2.5 -2.5 6 l 1 0 q -1 -3.5 2.5 -5 m -0.5 -1.5 l 1 0 l 0 1',
				height: 15,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_ARTIFACT: {
				d: 'm {mx},{my} m 3.5 1.5 l 6 0 l 5 5 l 0 10 l -11 0 l 0 -15 m 11 5 l -5 0 l 0 -5',
				height: 15,
				width: 11,
				heightElements: [],
				widthElements: [],
			},

			PICTO_NODE: {
				d: 'm {mx},{my} m 1.5 5.5 l 11 0 l 0 11 l -11 0 l 0 -11 l 4 -4 l 11 0 l 0 11 l -4 4 m 0 -11 l 4 -4',
				height: 15,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			PICTO_SYSTEM_SOFTWARE: {
				d: 'm {mx},{my} m 2.5 10 a 1 1 90 0 0 11 0 a 1 1 90 0 0 -11 0 m 7.29 -7.5 a 5.5 5.5 90 0 0 -4.544 2.7392 a 5.5 5.5 90 0 1 8.254 4.7608 a 5.5 5.5 90 0 1 -0.7353 2.7473 a 5.5 5.5 90 0 0 2.7353 -4.7473 a 5.5 5.5 90 0 0 -5.71 -5.5',
				height: 13,
				width: 13,
				heightElements: [],
				widthElements: [],
			},

			PICTO_PATH: {
				d: 'm {mx},{my} m 6 3.5 l -5 5.1 m 0 -0.2 l 5 5.099 m 6 -10 l 5 5.101 m 0 -0.2 l -5 5.099 m -9 -5 l 3 0 m 1.5 0 l 3 0 m 1.5 0 l 3 0', // m 6 3.5 l -5 5 m 0 0 l 5 5 m 6 -10 l 5 5 m 0 0 l -5 5 m -9 -5 l 3 0 m 1.5 0 l 3 0 m 1.5 0 l 3 0',
				height: 10,
				width: 16,
				heightElements: [],
				widthElements: [],
			},

			PICTO_COMMUNICATION_NETWORK: {
				d: 'm {mx},{my} m 1 13.5 a 1 1 90 0 0 5 0 a 1 1 90 0 0 -5 0 m 2 -9 a 1 1 90 0 0 5 0 a 1 1 90 0 0 -5 0 m 14 0 a 1 1 90 0 0 -5 0 a 1 1 90 0 0 5 0 m -7 9 a 1 1 90 0 0 5 0 a 1 1 90 0 0 -5 0 m -2 -9 l 4 0 m -6 9 l 4 0 m -6.5 -2.5 l 2 -4 m 7 4 l 2 -4',
				height: 13,
				width: 13,
				heightElements: [],
				widthElements: [],
			},

			PICTO_DEVICE: {
				d: 'm {mx},{my} m 5 11.5 l -3.5 4 l 15 0 l -3.5 -4 m -11.5 -2 l 0 -5 a 2 2 90 0 1 2 -2 l 11 0 a 2 2 90 0 1 2 2 l 0 5 a 2 2 90 0 1 -2 2 l -11 0 a 2 2 90 0 1 -2 -2',
				height: 13,
				width: 13,
				heightElements: [],
				widthElements: [],
			},

			PICTO_EQUIPMENT: {
				// Bounds: x 1..15 (w14), y 1..15 (h14)
				d: 'm {mx},{my} m 5.464 6.397 h 1.136 v -1.136 h 2.8 v 1.136 h 1.136 v 2.8 h -1.136 v 1.136 h -2.8 v -1.136 h -1.136 v -2.8 z m 3.936 6.736 v 1.867 h -2.8 v -1.867 h -3.733 v -4.138 h -1.867 v -2.8 h 1.867 v -3.328 h 3.733 v -1.867 h 2.8 v 1.867 h 3.733 v 3.328 h 1.867 v 2.8 h -1.867 v 4.138 h -3.733 z',
				height: 14,
				width: 14,
				heightElements: [],
				widthElements: [],
			},

			PICTO_FACILITY: {
				// Bounds: x 1..15 (w14), y 0.5..15.5 (h15)
				d: 'm {mx},{my} m 1 0.5 l 2 0 l 0 11.586 l 3.495 -3.495 l 0 3.909 l 0.253 0 l 3.909 -3.909 l 0 3.909 l 0.434 0 l 3.909 -3.909 l 0 6.909 l -12 0 l -2 0 l 0 -15 z',
				height: 15,
				width: 14,
				heightElements: [],
				widthElements: [],
			},

			PICTO_DISTRIBUTION_NETWORK: {
				d: 'm {mx},{my} m 1.328 8 l 1.687 -1.797 h 9.97 l 1.687 1.797 l -1.58 1.682 h -10.184 l -1.58 -1.682 z m 3.251 -3.536 l -3.251 3.536 m 3.251 3.536 l -3.251 -3.536 m 10.093 -3.536 l 3.251 3.536 m -3.251 3.536 l 3.251 -3.536',
				height: 13,
				width: 14,
				heightElements: [],
				widthElements: [],
			},

			PICTO_MATERIAL: {
				// Bounds: x 0.5..15.5 (w15), y 1.5..14.5 (h13)
				d: 'm {mx},{my} m 5.5 12.5 l 5 0 m -6.25 -11 l 7.499 0 l 3.75 6.496 l -3.749 6.495 l -7.5 0 l -3.75 -6.496 l 3.75 -6.495 z m -0.75 6.494 l 2.5 -4.5 m 6.5 4.5 l -2.5 -4.5',
				height: 13,
				width: 15,
				heightElements: [],
				widthElements: [],
			},

			/* Implementation & Migration */
			PICTO_DELIVRABLE: {
				d: 'm {mx},{my} m 1 1 h 9 v 7 c -2 0.5 -4 0.5 -6 -1 c -2 -1.5 -2 -0.5 -3 1 z',
				height: 7,
				width: 10,
				heightElements: [],
				widthElements: [],
			},
			PICTO_GAP: {
				d: 'm {mx},{my} m 0.836 7.104 l 14.328 0 m -14.328 1.792 l 14.328 0 m -13.164 -0.896 a 6 6 0 1 1 0 0.001',
				height: 12,
				width: 16,
				heightElements: [],
				widthElements: [],
			},
			PICTO_PLATEAU: {
				d: 'm {mx},{my} m 4.5 5.2 l 10.5 0 m -12.25 2.8 l 10.5 0 m -12.25 2.8 l 10.5 0',
				height: 6,
				width: 15,
				heightElements: [],
				widthElements: [],
			},
			PICTO_WORK_PACKAGE: {
				d: 'm {mx},{my} m 15.5 5.25 c 0 -1.518 -1.232 -2.75 -2.75 -2.75 l -9.5 0 c -1.518 0 -2.75 1.232 -2.75 2.75 l 0 5.5 c 0 1.518 1.232 2.75 2.75 2.75 l 9.5 0 c 1.518 0 2.75 -1.232 2.75 -2.75 l 0 -5.5 z',
				height: 11,
				width: 13,
				heightElements: [],
				widthElements: [],
			},

			FIGURE_OBJECT: {
				d: 'm {mx},{my} l 0,{e.y0} c {e.x0},{e.y1} {e.x1},-{e.y2} {e.x2},-{e.y3} c {e.x3},-{e.y4} {e.x4},{e.y5} {e.x5},{e.y6} l 0,-{e.y7}z',
				width: 98, //100
				height: 63, //65
				widthElements: [29.8, 48.4, 67.2, 12.2, 19.8, 30.8],
				heightElements: [55.3, 19.7, 4.2, 6.7, 2.3, 1.6, 6.2, 54.6],
			},

			FIGURE_NOTE: {
				d: 'm {mx},{my} h {e.x0} v {{e.y0}-15} l -15,15 h -{{e.x0}-15} v -{e.y0} z',
				width: 100,
				height: 70,
				widthElements: [100],
				heightElements: [70],
			},
		};
	}

	getRawPath(pathRef: string): string {
		return this.pathMap[pathRef].d;
	}

	/**
	 * Scales the path to the given height and width.
	 * <h1>Use case</h1>
	 * <p>Use case is to scale the content of elements (event, gateways) based
	 * on the element bounding box's size.
	 * </p>
	 * <h1>Why not transform</h1>
	 * <p>Scaling a path with transform() will also scale the stroke and IE does not support
	 * the option 'non-scaling-stroke' to prevent this.
	 * Also there are use cases where only some parts of a path should be
	 * scaled.</p>
	 *
	 * @param {String} pathId The ID of the path.
	 * @param {Object} param <p>
	 *   Example param object scales the path to 60% size of the container (data.width, data.height).
	 *   <pre>
	 *   {
	 *     xScaleFactor: 0.6,
	 *     yScaleFactor:0.6,
	 *     containerWidth: data.width,
	 *     containerHeight: data.height,
	 *     position: {
	 *       mx: 0.46,
	 *       my: 0.2,
	 *     }
	 *   }
	 *   </pre>
	 *   <ul>
	 *    <li>targetpathwidth = xScaleFactor * containerWidth</li>
	 *    <li>targetpathheight = yScaleFactor * containerHeight</li>
	 *    <li>Position is used to set the starting coordinate of the path. M is computed:
	 *    <ul>
	 *      <li>position.x * containerWidth</li>
	 *      <li>position.y * containerHeight</li>
	 *    </ul>
	 *    Center of the container <pre> position: {
	 *       mx: 0.5,
	 *       my: 0.5,
	 *     }</pre>
	 *     Upper left corner of the container
	 *     <pre> position: {
	 *       mx: 0.0,
	 *       my: 0.0,
	 *     }</pre>
	 *    </li>
	 *   </ul>
	 * </p>
	 *
	 */
	getScaledPath(pathRef: string, param: ScaledPathParam): string {
		const rawPath = this.pathMap[pathRef];

		// positioning
		// compute the start point of the path
		let mx, my;

		if (param.abspos) {
			mx = param.abspos.x;
			my = param.abspos.y;
		} else {
			mx = param.containerWidth * param.position.mx;
			my = param.containerHeight * param.position.my;
		}

		const coordinates: Record<string, number> = {}; // map for the scaled coordinates
		if (param.position) {
			// path
			const heightRatio = (param.containerHeight / rawPath.height) * param.yScaleFactor;
			const widthRatio = (param.containerWidth / rawPath.width) * param.xScaleFactor;

			// Apply height ratio
			for (let heightIndex = 0; heightIndex < rawPath.heightElements.length; heightIndex++) {
				coordinates['y' + heightIndex] = rawPath.heightElements[heightIndex] * heightRatio;
			}

			// Apply width ratio
			for (let widthIndex = 0; widthIndex < rawPath.widthElements.length; widthIndex++) {
				coordinates['x' + widthIndex] = rawPath.widthElements[widthIndex] * widthRatio;
			}
		}

		// Apply value to raw path
		return formatPathTemplate(rawPath.d, {
			mx: mx,
			my: my,
			e: coordinates,
		});
	}
}

const _pathMapForExports = new PathMap();

/**
 * d для PICTO_GROUPING (как getIconSvgPath/мини-палитра 24px): один источник с pathMap, без modeler.
 */
export function getPictoGroupingSvgD(containerWidth = 24, containerHeight = 24): string {
	return _pathMapForExports.getScaledPath('PICTO_GROUPING', {
		xScaleFactor: 1,
		yScaleFactor: 1,
		containerWidth,
		containerHeight,
		position: {mx: 0, my: 0},
	});
}

// helpers (Snap.svg / PathMap.js: `resolveFormatToken` = обход `e.x0`; `formatPathTemplate` = вложенные `{...}` + eval)

const tokenRegex = /\{(.[^{]+)\}/g;
const objNotationRegex = /(?:(?:^|\.)(.+?)(?=\[|\.|$|\()|\[('|")(.+?)\2\])(\(\))?/g;

/**
 * Подставляет одно вхождение `{path}`: путь `mx`, `e.x0` и т.д. (обход сегментов через `objNotationRegex`).
 */
function resolveFormatToken(fullMatch: string, keyPath: string, root: PathFormatContext): string {
	let res: unknown = root;

	keyPath.replace(
		objNotationRegex,
		function (
			_match: string,
			name: string | undefined,
			_quote: string | undefined,
			quotedName: string | undefined,
			isFunc: string | undefined
		) {
			// same as оригинал: `name` из группы path или `["quoted"]`
			const segment = name || quotedName;
			if (res) {
				if (segment != null && typeof res === 'object' && segment in res) {
					res = (res as Record<string, unknown>)[segment];
				}

				if (typeof res === 'function' && isFunc) res = (res as () => unknown)();
			}
			// значение не используется; для typings — как при `return undefined` в движке
			return '';
		}
	);

	const out = res == null || res === root ? fullMatch : res;
	return out + '';
}

/**
 * Подстановка `d`: `{mx}` / `{e.x0}`; для токенов вроде `{{e.y0}-15}` внутренняя подстановка и `eval` арифметики (FIGURE_NOTE).
 */
function formatPathTemplate(str: string, ctx: PathFormatContext): string {
	return String(str).replace(tokenRegex, (match, group: string): string => {
		if (!group.match(tokenRegex)) {
			return resolveFormatToken(match, group, ctx);
		}
		const val = group.replace(tokenRegex, (innerMatch, key: string) =>
			resolveFormatToken(innerMatch, key, ctx)
		);
		return String(
			// eslint-disable-next-line no-eval
			eval(val)
		);
	});
}
